import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError, TOKEN_KEY } from "@/lib/api";
import { Eye, EyeOff } from "lucide-react";
import { PW_RULES, pwStrong } from "@/components/PasswordField";

// Public Google OAuth client ID, baked in at build time. When unset, the
// "Continue with Google" button is simply not shown (email/password still works).
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

export default function Auth({ mode = "login" }) {
  const isLogin = mode === "login";
  const { login, register, setUser } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("buyer");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const nav = useNavigate();
  const googleBtnRef = useRef(null);

  // Phone-OTP login (login mode only).
  const [method, setMethod] = useState("email"); // "email" | "phone"
  const [phone, setPhone] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");

  const sendLoginOtp = async () => {
    if (phone.replace(/\D/g, "").length < 10) return setErr("Enter a valid 10-digit mobile number.");
    setErr(""); setBusy(true);
    try {
      await api.post("/auth/phone-login/start", { phone });
      setOtpSent(true);
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || "Couldn't send code."); }
    finally { setBusy(false); }
  };
  const phoneLogin = async () => {
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/phone-login", { phone, otp });
      if (data.token) sessionStorage.setItem(TOKEN_KEY, data.token);
      setUser(data);
      routeByRole(data);
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || "Couldn't sign in."); }
    finally { setBusy(false); }
  };

  const routeByRole = (u) => {
    if (u.role === "admin") nav("/admin");
    else if (u.role === "vendor") nav("/vendor");
    else nav("/account");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!isLogin && !pwStrong(password)) { setErr("Please choose a stronger password (see the requirements below)."); return; }
    setErr(""); setBusy(true);
    try {
      const u = isLogin
        ? await login(email, password)
        : await register({ email, password, name, role });
      routeByRole(u);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  // Keep the freshest handler in a ref so the (once-registered) Google callback
  // always sees current state setters.
  const onGoogleCredential = useRef();
  onGoogleCredential.current = async (resp) => {
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/google", { credential: resp.credential });
      if (data.token) sessionStorage.setItem(TOKEN_KEY, data.token);
      setUser(data);
      routeByRole(data);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Google sign-in failed.");
    } finally { setBusy(false); }
  };

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;
    const init = () => {
      if (!window.google?.accounts?.id || !googleBtnRef.current) return;
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: (resp) => onGoogleCredential.current(resp),
      });
      // Fit the button to its container so it never overflows on small screens
      // (Google caps width at 400).
      const cw = googleBtnRef.current.parentElement?.clientWidth || 360;
      window.google.accounts.id.renderButton(googleBtnRef.current, {
        type: "standard", theme: "outline", size: "large",
        text: "continue_with", shape: "rectangular",
        width: Math.max(240, Math.min(400, Math.floor(cw))),
      });
    };
    if (document.getElementById("google-gsi")) { init(); return; }
    const s = document.createElement("script");
    s.src = "https://accounts.google.com/gsi/client";
    s.async = true; s.defer = true; s.id = "google-gsi";
    s.onload = init;
    document.head.appendChild(s);
  }, []);

  return (
    <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-16 grid md:grid-cols-12 gap-12">
      <div className="md:col-span-6 hidden md:block">
        <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Access · Certif. № TS-2026</p>
        <h1 className="font-serif text-7xl leading-[0.9] tracking-tighter mt-4">
          {isLogin ? "Welcome back." : "Set up your tag."}
        </h1>
        <p className="mt-8 max-w-md text-muted-foreground">
          {isLogin
            ? "Every login opens the Vault. Every purchase is inspected before it ships."
            : "One profile. Three doors: buy, sell, or oversee. Choose your role now — you can add another later."}
        </p>
      </div>
      <form onSubmit={(e) => { e.preventDefault(); (isLogin && method === "phone") ? phoneLogin() : submit(e); }} className="md:col-span-6 max-w-md w-full">
        <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
          {isLogin ? "Sign in" : "Register"}
        </p>
        <h2 className="font-serif text-5xl mb-10 tracking-tighter">{isLogin ? "Log in." : "Join us."}</h2>

        {GOOGLE_CLIENT_ID && (
          <>
            <div data-testid="google-signin-btn" className="mb-6 flex justify-center">
              <div ref={googleBtnRef} />
            </div>
            <div className="flex items-center gap-3 mb-6">
              <div className="flex-1 h-px bg-foreground/20" />
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">or with email</span>
              <div className="flex-1 h-px bg-foreground/20" />
            </div>
          </>
        )}

        {isLogin && (
          <div className="flex gap-2 mb-6">
            {[{ k: "email", l: "Email" }, { k: "phone", l: "Mobile" }].map((m) => (
              <button key={m.k} type="button" onClick={() => { setMethod(m.k); setErr(""); }}
                className={`px-4 py-2 border font-mono text-[10px] uppercase tracking-widest ${method === m.k ? "bg-foreground text-background border-foreground" : "border-foreground/30 hover:border-foreground"}`}>{m.l}</button>
            ))}
          </div>
        )}

        {!isLogin && (
          <div className="mb-6">
            <label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Full Name</label>
            <input data-testid="name-input" value={name} onChange={(e) => setName(e.target.value)} className="underline-input" required />
          </div>
        )}

        {(!isLogin || method === "email") && (
          <>
            <div className="mb-6">
              <label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{isLogin ? "Email or username" : "Email"}</label>
              <input data-testid="email-input" type={isLogin ? "text" : "email"} value={email} onChange={(e) => setEmail(e.target.value)} className="underline-input" autoCapitalize="none" required />
            </div>
            <div className="mb-6">
              <label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Password</label>
              <div className="relative">
                <input data-testid="password-input" type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} className="underline-input pr-9" required />
                <button type="button" onClick={() => setShowPw((v) => !v)} aria-label={showPw ? "Hide password" : "Show password"}
                  className="absolute right-0 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-1">
                  {showPw ? <EyeOff size={16} strokeWidth={1.5} /> : <Eye size={16} strokeWidth={1.5} />}
                </button>
              </div>
              {!isLogin && password.length > 0 && (
                <ul className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1">
                  {PW_RULES.map((r) => {
                    const ok = r.test(password);
                    return (
                      <li key={r.key} className={`font-mono text-[10px] flex items-center gap-1.5 ${ok ? "text-[hsl(var(--accent))]" : "text-muted-foreground"}`}>
                        <span>{ok ? "✓" : "○"}</span>{r.label}
                      </li>
                    );
                  })}
                </ul>
              )}
              {isLogin && (
                <div className="text-right mt-2">
                  <Link to="/forgot-password" className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground underline hover:text-[hsl(var(--accent))]">Forgot password?</Link>
                </div>
              )}
            </div>
          </>
        )}

        {isLogin && method === "phone" && (
          <>
            <div className="mb-6">
              <label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Mobile number</label>
              <div className="flex gap-2 items-center">
                <input value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))} inputMode="numeric" placeholder="10-digit mobile" disabled={otpSent} className="underline-input flex-1" />
                <button type="button" onClick={sendLoginOtp} disabled={busy}
                  className="border border-foreground px-3 py-2 text-[10px] font-mono uppercase tracking-widest hover:bg-foreground hover:text-background disabled:opacity-40 whitespace-nowrap">{otpSent ? "Resend" : "Send code"}</button>
              </div>
            </div>
            {otpSent && (
              <div className="mb-6">
                <label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Enter code</label>
                <input value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" placeholder="6-digit code" className="underline-input" />
                <p className="mt-2 font-mono text-[10px] text-muted-foreground">Sent to your mobile if it's registered. New here? <Link to="/register" className="underline hover:text-[hsl(var(--accent))]">Create an account</Link>.</p>
              </div>
            )}
          </>
        )}
        {!isLogin && (
          <div className="mb-8">
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-3">I am a</p>
            <div className="grid grid-cols-2 gap-2">
              {[{ v: "buyer", n: "Buyer" }, { v: "vendor", n: "Vendor" }].map((r) => (
                <button key={r.v} type="button" data-testid={`role-${r.v}`} onClick={() => setRole(r.v)}
                  className={`py-3 border font-mono text-xs uppercase tracking-widest ${role === r.v ? "bg-foreground text-background border-foreground" : "border-foreground/40 hover:border-foreground"}`}>
                  {r.n}
                </button>
              ))}
            </div>
          </div>
        )}
        {err && <p className="mb-4 text-sm text-destructive font-mono">{err}</p>}
        {!(isLogin && method === "phone" && !otpSent) && (
          <button data-testid="submit-btn" disabled={busy || (isLogin && method === "phone" && otp.length < 6) || (!isLogin && !pwStrong(password))} type="submit"
            className="w-full bg-foreground text-background py-4 font-mono text-xs uppercase tracking-[0.24em] hover:bg-[hsl(var(--accent))] disabled:opacity-50">
            {busy ? "..." : (isLogin ? "Sign in" : "Create account")}
          </button>
        )}
        <p className="mt-6 font-mono text-[11px] text-muted-foreground">
          {isLogin ? "New here? " : "Have an account? "}
          <Link to={isLogin ? "/register" : "/login"} data-testid="switch-mode" className="underline hover:text-[hsl(var(--accent))]">
            {isLogin ? "Create an account" : "Sign in"}
          </Link>
        </p>
      </form>
    </div>
  );
}
